import { atom } from 'jotai';

import { planAtom } from './plan';

export type EntityKey = string;

export type EntityRef = { type: 'GROUP' | 'ROW'; id: number };

export type GridStore = {
  rootIds: EntityKey[];
  childrenMap: Record<EntityKey, EntityKey[]>;
  entities: Record<EntityKey, EntityRef>;
};

export const gridAtom = atom<GridStore>((get) => {
  const { groups, rows } = get(planAtom);

  const entities: Record<EntityKey, EntityRef> = {};
  const groupings: Record<string, EntityKey[]> = { root: [] };

  const addToGrouping = (key: EntityKey, parentKey: string) => {
    if (!groupings[parentKey]) {
      groupings[parentKey] = [];
    }
    groupings[parentKey].push(key);
  };

  for (const group of Object.values(groups)) {
    const key = `group-${group.id}`;
    entities[key] = { type: 'GROUP', id: group.id };
    const parentKey = group.parent_id ? `group-${group.parent_id}` : 'root';
    addToGrouping(key, parentKey);
  }

  for (const row of Object.values(rows)) {
    const key = `row-${row.id}`;
    entities[key] = { type: 'ROW', id: row.id };
    const parentKey = row.group_id ? `group-${row.group_id}` : 'root';
    addToGrouping(key, parentKey);
  }

  const childrenMap: Record<EntityKey, EntityKey[]> = {};
  const rootIds: EntityKey[] = [];

  for (const [parentKey, children] of Object.entries(groupings)) {
    children.sort((a, b) => {
      const aRef = entities[a];
      const bRef = entities[b];
      const aData = aRef.type === 'GROUP' ? groups[aRef.id] : rows[aRef.id];
      const bData = bRef.type === 'GROUP' ? groups[bRef.id] : rows[bRef.id];
      return aData.order_index - bData.order_index;
    });

    if (parentKey === 'root') {
      rootIds.push(...children);
    } else if (entities[parentKey]) {
      childrenMap[parentKey] = children;
    }
  }

  return { entities, rootIds, childrenMap };
});
